import React, { useEffect, useState } from 'react'
import { Collapse } from '@mui/material'
import { useGetCompanyInvitationsQuery, useGetInvitationsLimitsQuery } from '@redux/features/users/usersApi'
import { Can, useAbility } from "@/casl.config";

import {ReactComponent as ResendEmailSVG} from "@assets/icons/ic_resend_email.svg"
import {ReactComponent as RevokeEmailSVG} from "@assets/icons/ic_revoke_email.svg"

import BaseTable from "@reusables/BaseTable";
import NoPermissionBanner from "@components/ErrorPages/NoPermissionBanner";
import { cn } from '@/helpers/utils';
import { Users } from '@/types/general';
import ResendInviteModal from './modals/ResendInviteModal';
import RevokeInviteModal from './modals/RevokeInviteModal';
import BasePermissionBlocker from '@reusables/BasePermissionBlocker';
import BaseTooltip from '@reusables/BaseTooltip';
import { useTranslation } from 'react-i18next';
import InfoIcon from '@mui/icons-material/Info';
import ResendInviteTimer from './parts/ResendInviteTimer';
import { ExpiryCountdown, InvitationWithExpiry } from './parts/ExpiryCountdown';

export default function InvitedUsers() {
    const { t } = useTranslation("", { keyPrefix: "settings.general.companyInformation.tabs.users.inviteUsersTable" });

    const ability = useAbility();
    const allowedActions = ability.can("user.invite", "general");
    const { data: invitationsData, isLoading } = useGetCompanyInvitationsQuery();

    const { data: invitationsLimit } = useGetInvitationsLimitsQuery()

    const [ resendInvite, setResendInvite ] = useState<Users.DTO.CompanyInvitations>();
    const [ revokeInvite, setRevokeInvite ] = useState<Users.DTO.CompanyInvitations>();

    const [ isResendInviteModalOpen, setIsResendInviteModalOpen ] = useState(false);
    const [ isRevokeInviteModalOpen, setIsRevokeInviteModalOpen ] = useState(false);
    const [invitationsWithExpiry, setInvitationsWithExpiry] = useState<InvitationWithExpiry[]>([]);

    useEffect(() => {
        if (invitationsData && invitationsLimit?.expiration_days !== undefined) {
            const updatedInvitations = invitationsData.map(invitation => ({
                ...invitation,
                expiryStatus: <ExpiryCountdown invitedAt={invitation.last_sent_at} expirationDays={invitationsLimit.expiration_days} />,
            }));
            setInvitationsWithExpiry(updatedInvitations);
        }
    }, [invitationsData, invitationsLimit?.expiration_days]);

    return (
        <>
            <Collapse in={!!invitationsData?.length}>
                <Can not I="user.view" a="general">
                    <NoPermissionBanner />
                </Can>
                <Can I="user.view" a="general">
                    <div className='flex items-center space-x-2 mb-4'>
                        <div className="font-bold text-2xl text-accent">{t("heading")}</div>
                        <BaseTooltip 
                            title={<div className="text-sm">
                                <p className='m-0'>{t("table.tooltips.expirationDaysLimit", {days: invitationsLimit?.expiration_days})}</p>
                                <p className='m-0'>{t("table.tooltips.resendInviteLimit", {timeout: invitationsLimit?.resend_timeout ? (invitationsLimit?.resend_timeout / 60) : ""})}</p>
                                </div>}
                            placement="top">
                            <div>
                                <InfoIcon className='text-[#D9DAFD] hover:cursor-pointer'/>
                            </div>
                        </BaseTooltip>
                    </div>

                    
                    <BaseTable 
                        data={invitationsWithExpiry ?? []}
                        columns={[
                            {
                                header: t("table.columns.0"),
                                getter: row => row.email
                            },
                            {
                                header: t("table.columns.1"),
                                getter: row => row.role.name,
                            },
                            {
                                header: t("table.columns.2"),
                                getter: row => row.expiryStatus as string,
                            },
                            {
                                header: () => "",
                                getter: (row) => (
                                    <div className="flex justify-end items-center space-x-2 text-tables-secondaryIcon">
                                        <BasePermissionBlocker action="user.edit" module="general">
                                            <ResendInviteTimer row={row}>
                                                <ResendEmailSVG
                                                    className={cn("cursor-pointer w-5 h-5", {
                                                        "opacity-50 cursor-not-allowed": ability.cannot("user.edit", "general")
                                                    })}
                                                    onClick={() => {
                                                        if (allowedActions) {
                                                            setResendInvite(row);
                                                            setIsResendInviteModalOpen(true);
                                                        }
                                                    }}
                                                />
                                            </ResendInviteTimer>
                                        </BasePermissionBlocker>

                                        <BasePermissionBlocker action="user.edit" module="general">
                                            <BaseTooltip active={allowedActions} title={t("table.tooltips.revokeInvite") as string} placement="bottom">
                                                <RevokeEmailSVG
                                                    className={cn("w-5 h-5 cursor-pointer hover:text-tables-highlightedIcon", {
                                                                    "opacity-50 cursor-not-allowed": ability.cannot("user.edit", "general")
                                                                })}
                                                    onClick={() => {
                                                        if (allowedActions) {
                                                            setRevokeInvite(row);
                                                            setIsRevokeInviteModalOpen(true);
                                                        }
                                                    }}
                                                />
                                            </BaseTooltip>
                                        </BasePermissionBlocker>
                                    </div>
                                ),
                                sx: {
                                    width: "150px"
                                }
                            }
                        ]}
                        isDataLoading={isLoading}

                        headerSx={{
                            backgroundColor: "#F9F9F9"
                        }}
                        size='small'
                    />
                </Can>
            </Collapse>

            <ResendInviteModal
                isOpen={!!isResendInviteModalOpen}
                entity={resendInvite}
                onClose={() => {
                    setIsResendInviteModalOpen(false);
                }}
            />

            <RevokeInviteModal
                isOpen={!!isRevokeInviteModalOpen}
                entity={revokeInvite}
                onClose={() => {
                    setIsRevokeInviteModalOpen(false);
                }}
            />
        </>
    )
}
